// Pure helpers for the Qualify AURA Step C flow:
//   Optimal Blue register → Sapiens Decision Assist (run-aura) → poll CX.AUS.RESULT.
// Kept free of React/RTK so they can be unit-tested with injected callers.
// See docs/enhancement/sapiensandauraflow/[DRAFT]_AURA_flow_LoganConnect.drawio.
import {
	OB_QUEUE_POLL_INTERVAL_MS,
	OB_QUEUE_POLL_TIMEOUT_MS,
	OB_PUSH_IN_QUEUE_RETRY_MS,
	OB_PUSH_IN_QUEUE_MAX_RETRIES,
	RULES_ENGINE_POLL_INTERVAL_MS,
	RULES_ENGINE_POLL_TIMEOUT_MS,
	AURA_RESULT_NEGATED_POSITIVE_RE,
	AURA_RESULT_NEGATIVE_RE,
} from "./aura.constants";
import { isObPushInQueue } from "./conflict-error";

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Error carrying an error-messages.json key for CommanErrorModal. `cause` is the original
// API error, so callers can still tell a real 409 conflict apart.
export class AuraFlowError extends Error {
	constructor(errorKey, message, cause) {
		super(message || errorKey);
		this.errorKey = errorKey;
		this.cause = cause;
	}
}

// Repeatedly calls fetchStatus until isDone(value) or the timeout elapses.
// Resolves { status: "done" | "timeout" | "cancelled", value }. fetchStatus errors propagate.
export const pollUntil = async ({
	fetchStatus,
	isDone,
	intervalMs,
	timeoutMs,
	wait = delay,
	now = () => Date.now(),
	isCancelled = () => false,
}) => {
	const startedAt = now();
	let value;
	for (;;) {
		if (isCancelled()) return { status: "cancelled", value };
		value = await fetchStatus();
		if (isDone(value)) return { status: "done", value };
		if (now() - startedAt + intervalMs > timeoutMs) return { status: "timeout", value };
		await wait(intervalMs);
	}
};

// aura-status poll target: Sapiens finished (or failed) and, for a successful run,
// the findings PDF is in the eFolder — the backend reports findingsPending while
// CX.AUS.RESULT is final but the PDF hasn't landed yet.
export const isAuraStatusDone = (status) =>
	!!status?.completed && (!!status?.failed || !status?.findingsPending);

// Step 7b: reads CX.WRE.LASTRUN (rules-engine-status) until two reads in a row match.
// canSettle (optional) gates the match: while it returns false (7a has no result yet, so
// Sapiens may not have written anything) a matching pair doesn't count.
// Resolves like pollUntil; value is the latest { lastRun } response (also on timeout).
export const pollRulesEngineUntilSettled = ({ fetchLastRun, canSettle = () => true, ...options }) => {
	let previousLastRun;
	let readAfterGate = false;
	return pollUntil({
		fetchStatus: () => {
			readAfterGate = !!canSettle();
			return fetchLastRun();
		},
		isDone: (value) => {
			const lastRun = value?.lastRun ?? "";
			const settled = readAfterGate && previousLastRun !== undefined && lastRun === previousLastRun;
			previousLastRun = lastRun;
			return settled;
		},
		intervalMs: RULES_ENGINE_POLL_INTERVAL_MS,
		timeoutMs: RULES_ENGINE_POLL_TIMEOUT_MS,
		...options,
	});
};

// run-aura failure → error-messages.json key, from the failed step the backend reports in
// error.reason. null = no specific message (caller falls back to the conflict / generic one).
const AURA_RUN_REASON_KEYS = {
	loan_locked: "AURA_LOAN_LOCKED",
	token_failed: "AURA_TOKEN_FAILED",
	token_timeout: "AURA_TOKEN_FAILED",
	decision_event_failed: "AURA_DECISION_FAILED",
	decision_event_timeout: "AURA_DECISION_FAILED",
	loan_not_found: "AURA_LOAN_NOT_FOUND",
};
export const auraRunErrorKey = (err) => AURA_RUN_REASON_KEYS[err?.data?.error?.reason] || null;

// run-aura can outlive a proxy / gateway (502 / 504) or the browser's connection while the
// backend keeps waiting on Sapiens, so the run may still have been accepted.
export const isGatewayOrNetworkError = (err) =>
	// A gateway's HTML error page arrives as PARSING_ERROR with originalStatus 502 / 504.
	[err?.status, err?.originalStatus].some((value) => [502, 504].includes(Number(value))) ||
	["FETCH_ERROR", "TIMEOUT_ERROR"].includes(err?.status);

// Minimum Submission conditions with no document linked yet (PathA-6.06 submit gate).
export const countMinimumRemaining = (conditions) =>
	(conditions?.minimumSubmission || []).filter((c) => !(Number(c?.docsCount) > 0)).length;

// "AURA completed in X min Y sec" (drawio: stopTimeFull - startTimeFull).
export const formatElapsed = (ms) => {
	const totalSeconds = Math.max(0, Math.round((Number(ms) || 0) / 1000));
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes} min ${seconds} sec`;
};

export const normalizeAuraResult = (result) => {
	const value = String(result ?? "").trim().toLowerCase();
	if (!value) return "error";
	if (AURA_RESULT_NEGATED_POSITIVE_RE.test(value) || AURA_RESULT_NEGATIVE_RE.test(value)) return "ineligible";
	if (/error/.test(value)) return "error";
	if (/(elig|approv|accept|pass)/.test(value)) return "eligible";
	return value;
};

// Identifies one Optimal Blue registration (product + rate row) so a re-run of
// Step C with the same selection doesn't register the program again.
export const buildObRegistrationKey = (productId, row) =>
	[productId, row?.rate, row?.price, row?.lockPeriod]
		.map((part) => String(part ?? "").trim())
		.join("|");

// Optimal Blue push statuses that end the wait ("Successful", "Failed", "Error").
const OB_PUSH_FINAL_STATUS_RE = /success|fail|error/;

const obFormData = (eligibleData) => {
	const formData = new FormData();
	formData.append("ob_loan_id", eligibleData?.ob_loan_id);
	formData.append("ob_business_channel_id", eligibleData?.ob_business_channel_id);
	formData.append("ob_originator_id", eligibleData?.ob_originator_id);
	return formData;
};

// Registers the selected product with Optimal Blue and waits for the push to
// Encompass — same three calls as Add New Loan's Program & Pricing step
// (components/loan-form/loan-pricing-application handleSubmitPricingModal/checkQueueStatus).
// Resolves on success; throws AuraFlowError with an error-messages.json key otherwise.
export const registerObProgram = async ({
	updateLoanWithProduct,
	pushDataToEncompass,
	checkQueueStatus,
	eligibleData,
	productId,
	selectedRow,
	loanGuid,
	loanNumber,
	sourceArea,
	wait = delay,
	now,
}) => {
	const formData = obFormData(eligibleData);
	formData.append("product_id", productId);
	formData.append("search_id", eligibleData?.searchId);
	formData.append("rate", selectedRow?.rate);
	formData.append("price", selectedRow?.price);
	formData.append("lock_period", selectedRow?.lockPeriod);
	formData.append("loanguid", loanGuid);
	formData.append("encompass_loan_number", loanNumber || "");
	formData.append("source_area", sourceArea);

	// Optimal Blue 409 "Push is in queue." clears on its own — wait and retry the call.
	const withPushQueueRetry = async (call) => {
		for (let attempt = 0; ; attempt += 1) {
			try {
				return await call();
			} catch (err) {
				if (!isObPushInQueue(err)) {
					throw new AuraFlowError("PRODUCT_UPDATE_FAILED_SYSTEM", err?.data?.detail, err);
				}
				if (attempt >= OB_PUSH_IN_QUEUE_MAX_RETRIES) {
					throw new AuraFlowError("OB_PUSH_IN_QUEUE", err?.data?.data?.Message, err);
				}
				await wait(OB_PUSH_IN_QUEUE_RETRY_MS);
			}
		}
	};

	const updated = await withPushQueueRetry(() => updateLoanWithProduct({ formData }));
	if (!updated?.message?.toLowerCase()?.includes("success")) {
		throw new AuraFlowError("PRODUCT_UPDATE_FAILED_SYSTEM");
	}

	const pushed = await withPushQueueRetry(() =>
		pushDataToEncompass({ formData: obFormData(eligibleData) })
	);
	if (pushed?.status !== "Success") {
		const pushError = String(pushed?.error || "");
		if (pushError.includes("Encompass")) throw new AuraFlowError("PRODUCT_UPDATE_FAILED_ENCOMPASS");
		if (pushError.includes("OB") || pushError.includes("Optimal Blue")) {
			throw new AuraFlowError("PRODUCT_UPDATE_FAILED_OB");
		}
		throw new AuraFlowError("PRODUCT_UPDATE_FAILED_SYSTEM");
	}

	const queueId = pushed?.data?.pushQueueId;
	const queueStatus = (response) =>
		String(
			(response?.data || []).find((item) => item?.pushId === queueId?.toString())
				?.pushRequestStatus || ""
		).toLowerCase();

	let polled;
	try {
		polled = await pollUntil({
			fetchStatus: () => {
				const queueForm = obFormData(eligibleData);
				queueForm.append("ob_queue_id", queueId);
				return checkQueueStatus({ queueId, formData: queueForm });
			},
			// Only a final status ends the wait: an empty list (push ID not there yet),
			// "InProgress" or "Queued" keeps polling.
			isDone: (response) => OB_PUSH_FINAL_STATUS_RE.test(queueStatus(response)),
			intervalMs: OB_QUEUE_POLL_INTERVAL_MS,
			timeoutMs: OB_QUEUE_POLL_TIMEOUT_MS,
			wait,
			...(now ? { now } : {}),
		});
	} catch (err) {
		throw new AuraFlowError("PRICING_PROCESSING_TEAM_WORKING", err?.data?.detail, err);
	}
	if (polled.status !== "done") throw new AuraFlowError("PRICING_PROCESSING");
	if (!/^success/.test(queueStatus(polled.value))) {
		throw new AuraFlowError("AURA_OB_REGISTRATION_FAILED");
	}
	return true;
};
